import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment-timezone'
import { Link, useHistory, useLocation } from 'react-router-dom'
import {
  List,
  Card,
  Row,
  Typography,
  Tag,
  Divider,
  Tabs as Tab,
  Form,
  Select,
} from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import Tabs from '../../components/Tabs'
import Table from '../../components/Tables/SearchTable'

import {
  DEFAULT_IMPORT_LOGS_PAGE_SIZE,
  DEFAULT_IMPORT_LOGS_PAGE_SIZE_OPTIONS,
} from '../../../constants'

import PageLayout from '../../layouts/DetailPageLayout'
import ImportLogCardInfoItem from './ImportLogCardInfoItem'
import {
  mapImportLogsStatusColor,
  formatDateTime,
  capitalizeEveryWord,
} from '../../../utils/helpers'
import useDispatchHttp from '../../../hooks/dispatchHttpHandler'

import {
  selectAllImportLogs,
  isFetchingImportLogs,
  importLogsCount,
  selectAllExtraImportLogsTypes,
  getImportLogsList,
  getImportLogFile,
  getWeeklyNpeesUpdatesList,
  getExtraImportLogsList,
  getExtraImportLogFile,
  getExtraImportLogsTypes,
} from '../../../state/modules/importLogs'

const tabs = [
  { name: 'Completed Cases', key: 1 },
  { name: 'New Cases', key: 0 },
  { name: 'Pending Cases', key: 2 },
  { name: 'Weekly NPPES Updates', key: 3 },
  { name: 'Extra Imports', key: 4 },
]

const caseItems = {
  0: [
    'total',
    'mapped',
    'success',
    'rejected',
    'warnings',
    'skipped',
    'mapping_ratio',
    'mapping_time',
    'started_at',
  ],
  1: [
    'total',
    'mapped',
    'success',
    'rejected',
    'warnings',
    'skipped',
    'mapping_ratio',
    'mapping_time',
    'started_at',
  ],
  2: [
    'total',
    'skipped',
    'reprocessed',
    'created',
    'success',
    'rejected',
    'warnings',
    'mapping_ratio',
    'mapping_time',
    'started_at',
  ],
}

const ImportLogs = () => {
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()
  const dispatchHttp = useDispatchHttp()

  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  )

  const initialTab = queryParams.get('tab') ? Number(queryParams.get('tab')) : 1

  const initialPage = queryParams.get('page') ? Number(queryParams.get('page')) : 1

  const defaultPageSize =
    initialTab === 4 || initialTab === 3 ? 10 : DEFAULT_IMPORT_LOGS_PAGE_SIZE
  const initialPageSize = queryParams.get('pageSize')
    ? Number(queryParams.get('pageSize'))
    : defaultPageSize

  const initialImportType =
    initialTab === 4 && queryParams.get('import_type')
      ? Number(queryParams.get('import_type'))
      : 1

  const [activeTab, setActiveTab] = useState(initialTab)
  const [currentPage, setCurrentPage] = useState(initialPage)
  const [currentPageSize, setCurrentPageSize] = useState(initialPageSize)
  const [selectedImportType, setSelectedImportType] = useState(initialImportType)

  useEffect(() => {
    if (
      queryParams.has('tab') ||
      queryParams.has('page') ||
      queryParams.has('pageSize') ||
      queryParams.has('import_type')
    ) {
      history.replace({
        pathname: location.pathname,
        search: '',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleListChange = useCallback(
    (page, pageSize) => {
      setCurrentPage(page)
      setCurrentPageSize(pageSize)

      if (activeTab === 3) {
        dispatchHttp(getWeeklyNpeesUpdatesList({ page, pageSize }))
        return
      }
      if (activeTab === 4) {
        dispatchHttp(
          getExtraImportLogsList({
            page,
            pageSize,
            import_type: selectedImportType,
          })
        )
        return
      }
      dispatchHttp(getImportLogsList({ page, pageSize, case_type: activeTab }))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatchHttp, activeTab, selectedImportType]
  )

  const getDurationData = item => {
    const finishedAt = item.finished_at
    let minutesSeconds = ''
    let hours = ''

    if (finishedAt) {
      const start = moment(item.started_at)
      const end = moment(finishedAt)
      const diff = end.diff(start)
      const diffMilliseconds = moment.duration(diff).asMilliseconds()
      minutesSeconds = moment.utc(diffMilliseconds).format('mm:ss')
      hours = Math.floor(moment.duration(diffMilliseconds).asHours())
    }

    return {
      finishedAt,
      minutesSeconds,
      hours,
    }
  }

  const importLogsColumns = [
    {
      title: 'Started At',
      key: 'started_at',
      width: 140,
      date: true,
    },
    {
      title: 'Status',
      key: 'status',
      render: (text, column, { status }) => (
        <Tag color={mapImportLogsStatusColor(status)}>{status}</Tag>
      ),
      width: 100,
    },
    {
      title: 'File Name',
      key: 'cms_file',
      render: (text, column, data) => data?.cms_file?.file_name,
    },
    {
      title: 'Duration',
      key: 'started_at',
      render: (text, column, item) => {
        const { finishedAt, minutesSeconds, hours } = getDurationData(item)
        return <span>{finishedAt ? `${hours}:${minutesSeconds}` : '-'}</span>
      },
    },
    {
      title: 'Total Cases',
      key: 'total_cases_in_file',
    },
    {
      title: 'Mapped',
      key: 'total_cases_mapped',
    },
    {
      title: 'Success',
      key: 'total_cases_success',
      render: value => (
        <ImportLogCardInfoItem
          value={value}
          type={ImportLogCardInfoItem.STATUSES.POSITIVE}
        />
      ),
    },
    {
      title: 'Rejected',
      key: 'total_cases_rejected',
      render: value => (
        <ImportLogCardInfoItem
          value={value}
          type={ImportLogCardInfoItem.STATUSES.NEGATIVE}
        />
      ),
    },
    {
      title: 'Warnings',
      key: 'total_exceptions',
      render: value => (
        <ImportLogCardInfoItem
          value={value}
          type={ImportLogCardInfoItem.STATUSES.NEGATIVE}
        />
      ),
    },
    {
      title: 'Skipped',
      key: 'total_cases_skipped',
    },
    {
      title: 'Case Mapping Ratio',
      key: 'cmr',
      render: value => <span>{value}%</span>,
    },
    {
      title: 'Average Case Mapping Time',
      key: 'avg_case_mapping_time',
      render: value => {
        let averageTimeMapping = '-'
        if (value) {
          averageTimeMapping = `${parseFloat(value).toFixed(3)}s`
        }
        return <span>{averageTimeMapping}</span>
      },
    },
    {
      key: 'id',
      render: (text, column, item) => (
        <Typography.Link
          strong
          onClick={() => {
            dispatch(getImportLogFile(item.id, item?.cms_file?.file_name))
          }}
        >
          Download <DownloadOutlined />
        </Typography.Link>
      ),
      width: 104,
      onCell: () => ({
        className: 'do-not-navigate',
      }),
    },
  ]

  const importLogs = useSelector(selectAllImportLogs)
  const loading = useSelector(isFetchingImportLogs)
  const tableLoadingStatus = useSelector(state => state.importLogs.status)
  const count = useSelector(importLogsCount)
  const importTypes = useSelector(selectAllExtraImportLogsTypes)
  const [extraImportLogsTypesOptions, setExtraImportLogsTypesOptions] = useState([])

  useEffect(() => {
    let typesOptions = []

    typesOptions = importTypes.map(item => ({
      label: capitalizeEveryWord(item.name),
      value: item.id,
    }))

    setExtraImportLogsTypesOptions(typesOptions)
  }, [importTypes])

  const handleSelect = value => {
    setSelectedImportType(value)
    setCurrentPage(1)

    dispatchHttp(
      getExtraImportLogsList({
        page: 1,
        pageSize: currentPageSize,
        import_type: value,
      })
    )
  }

  useEffect(() => {
    if (activeTab === 3) {
      dispatchHttp(
        getWeeklyNpeesUpdatesList({ page: currentPage, pageSize: currentPageSize })
      )
      return
    }

    if (activeTab === 4) {
      dispatchHttp(getExtraImportLogsTypes())
      dispatchHttp(
        getExtraImportLogsList({
          import_type: selectedImportType,
          page: currentPage,
          pageSize: currentPageSize,
        })
      )
      return
    }

    dispatchHttp(
      getImportLogsList({
        page: currentPage,
        pageSize: currentPageSize,
        case_type: activeTab,
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatchHttp, activeTab, selectedImportType])

  const renderWeeklyNpeesUpdatesItem = useCallback(
    item => {
      const fileNameParts = item?.file_url?.split('/') || []
      const fileName = fileNameParts[fileNameParts.length - 1]
      return (
        <List.Item key={item.id}>
          <Card style={{ width: '100%' }}>
            <Row align="middle">
              <div className="import_log_card__title">
                <span className="import_log_card__title_id">#{item.id}</span>{' '}
                {formatDateTime(item.created_at)}
              </div>
              <Tag color={mapImportLogsStatusColor(item.status)}>{item.status}</Tag>
            </Row>
            <Row align="middle">
              <Link
                to={{
                  pathname: `/data-sync-log-weekly-npees-updates/${item.id}`,
                  state: {
                    returnTab: 3,
                    returnPage: currentPage,
                    returnPageSize: currentPageSize,
                  },
                }}
                className="import_log_card__file_name"
              >
                {fileName || 'No file'}
              </Link>
            </Row>
            <Row align="middle">
              <Typography.Link strong href={item?.file_url}>
                Download <DownloadOutlined />
              </Typography.Link>
              <Divider type="vertical" />
              <ImportLogCardInfoItem
                title="Total Doctors Imported:"
                value={item.total_doctors_imported}
              />
              <ImportLogCardInfoItem
                title="Total Facilities Imported:"
                value={item.total_facilities_imported}
              />
            </Row>
          </Card>
        </List.Item>
      )
    },
    [currentPage, currentPageSize]
  )

  const renderExtraImportLogsItem = useCallback(
    item => {
      const {
        data_file,
        finished_at,
        started_at,
        id,
        import_type,
        status,
        duration,
        ...rest
      } = item

      const isClickable = selectedImportType !== 0 && selectedImportType !== 1

      return (
        <List.Item key={item.id}>
          <Card style={{ width: '100%' }}>
            <Row align="middle">
              <div className="import_log_card__title">
                <span className="import_log_card__title_id">#{id}</span>{' '}
                {formatDateTime(started_at)}
              </div>
              <Tag color={mapImportLogsStatusColor(status)}>{status}</Tag>
            </Row>
            <Row align="middle">
              {isClickable ? (
                <Link
                  to={{
                    pathname: `/vendor-import-log/${id}`,
                    state: {
                      returnPage: currentPage,
                      returnPageSize: currentPageSize,
                      returnImportType: selectedImportType,
                    },
                    search: `?import_type=${selectedImportType}`,
                  }}
                  className="import_log_card__file_name"
                >
                  {data_file?.file_name || 'No file'}
                </Link>
              ) : (
                <span className="import_log_card__file_name">
                  {' '}
                  {data_file?.file_name || 'No file'}
                </span>
              )}
            </Row>
            <Row align="middle">
              <Typography.Link
                strong
                onClick={() => {
                  dispatch(getExtraImportLogFile(id, data_file?.file_name))
                }}
              >
                Download <DownloadOutlined />
              </Typography.Link>
              <Divider type="vertical" />

              {Object.keys(rest).map(name => {
                const data = rest[name]
                if (typeof rest[name] === 'object') return null
                const title = name?.replace(/_/g, ' ')

                return (
                  <ImportLogCardInfoItem
                    key={`${id}-${name}`}
                    title={`${capitalizeEveryWord(title)}:`}
                    value={data}
                  />
                )
              })}
              <ImportLogCardInfoItem title="Duration:" value={duration || '-'} />
            </Row>
          </Card>
        </List.Item>
      )
    },
    [dispatch, selectedImportType, currentPage, currentPageSize]
  )

  const getCaseElement = useCallback((item, type) => {
    const ratio = `${item.cmr}%`
    const { finishedAt, minutesSeconds, hours } = getDurationData(item)
    let averageTimeMapping = '-'
    if (item.avg_case_mapping_time) {
      averageTimeMapping = `${parseFloat(item.avg_case_mapping_time).toFixed(3)}s`
    }

    const mapping = {
      total: (
        <ImportLogCardInfoItem title="Total Cases:" value={item.total_cases_in_file} />
      ),
      mapped: <ImportLogCardInfoItem title="Mapped:" value={item.total_cases_mapped} />,
      success: (
        <ImportLogCardInfoItem
          title="Success:"
          value={item.total_cases_success}
          type={ImportLogCardInfoItem.STATUSES.POSITIVE}
        />
      ),
      rejected: (
        <ImportLogCardInfoItem
          title="Rejected:"
          value={item.total_cases_rejected}
          type={ImportLogCardInfoItem.STATUSES.NEGATIVE}
        />
      ),
      warnings: (
        <ImportLogCardInfoItem
          title="Warnings:"
          value={item.total_exceptions}
          type={ImportLogCardInfoItem.STATUSES.NEGATIVE}
        />
      ),
      reprocessed: (
        <ImportLogCardInfoItem
          title="Reprocessed:"
          value={item.total_cases_reprocessed}
        />
      ),
      created: (
        <ImportLogCardInfoItem title="Created:" value={item.total_cases_created} />
      ),
      skipped: (
        <ImportLogCardInfoItem title="Skipped:" value={item.total_cases_skipped} />
      ),
      mapping_ratio: <ImportLogCardInfoItem title="Case Mapping Ratio:" value={ratio} />,
      mapping_time: (
        <ImportLogCardInfoItem
          title="Average Case Mapping Time:"
          value={averageTimeMapping}
        />
      ),
      started_at: (
        <ImportLogCardInfoItem
          title="Duration:"
          value={finishedAt ? `${hours}:${minutesSeconds}` : '-'}
        />
      ),
    }

    return mapping[type]
  }, [])

  const renderItem = useCallback(
    item => {
      if (activeTab === 3) {
        return renderWeeklyNpeesUpdatesItem(item)
      }
      if (activeTab === 4) {
        return renderExtraImportLogsItem(item)
      }

      return (
        <List.Item key={item.id}>
          <Card style={{ width: '100%' }}>
            <Row align="middle">
              <div className="import_log_card__title">
                <span className="import_log_card__title_id">#{item.id}</span>{' '}
                {formatDateTime(item.started_at)}
              </div>
              <Tag color={mapImportLogsStatusColor(item.status)}>{item.status}</Tag>
            </Row>
            <Row align="middle">
              <Link
                to={{
                  pathname: `/data-sync-log/${item.id}`,
                  state: {
                    returnTab: activeTab,
                    returnPage: currentPage,
                    returnPageSize: currentPageSize,
                  },
                }}
                className="import_log_card__file_name"
              >
                {item?.cms_file?.file_name}
              </Link>
            </Row>
            <Row align="middle">
              <Typography.Link
                strong
                onClick={() => {
                  dispatch(getImportLogFile(item.id, item?.cms_file?.file_name))
                }}
              >
                Download <DownloadOutlined />
              </Typography.Link>
              <Divider type="vertical" />
              {caseItems[activeTab].map(type => getCaseElement(item, type))}
            </Row>
          </Card>
        </List.Item>
      )
    },
    [
      dispatch,
      activeTab,
      renderWeeklyNpeesUpdatesItem,
      renderExtraImportLogsItem,
      getCaseElement,
      currentPage,
      currentPageSize,
    ]
  )

  return (
    <PageLayout title="CMS Imports Logs">
      <Tabs
        fullWidth
        theme="dark"
        tabsProps={{
          size: 'small',
          tabBarGutter: 24,
          onChange: key => {
            const newTab = +key
            setCurrentPage(1)
            setActiveTab(newTab)
            setSelectedImportType(1)
            const defaultSize =
              newTab === 4 || newTab === 3 ? 10 : DEFAULT_IMPORT_LOGS_PAGE_SIZE
            setCurrentPageSize(defaultSize)
          },
          activeKey: `${activeTab}`,
        }}
      >
        {tabs.map(({ key, name }) => (
          <Tab.TabPane tab={name} key={key}>
            <>
              {activeTab === 4 && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    margin: 0,
                  }}
                >
                  <Form
                    initialValues={{ import_type: selectedImportType }}
                    layout="inline"
                    size="small"
                  >
                    <Form.Item
                      label="Import Type"
                      className="sort-form__select-field"
                      name="import_type"
                      style={{ alignItems: 'center' }}
                    >
                      <Select
                        size="middle"
                        className="sort-form__select"
                        onChange={handleSelect}
                        options={extraImportLogsTypesOptions}
                        placeholder="Select here"
                        loading={loading}
                        style={{ width: 160 }}
                        value={selectedImportType}
                      />
                    </Form.Item>
                  </Form>
                </div>
              )}
              {activeTab === 3 || activeTab === 4 ? (
                <List
                  dataSource={importLogs}
                  renderItem={renderItem}
                  pagination={{
                    current: currentPage,
                    pageSize: currentPageSize,
                    onChange: handleListChange,
                    total: count,
                    hideOnSinglePage: importLogs.length === 0,
                  }}
                  loading={loading}
                />
              ) : (
                <Table
                  onLoadData={getImportLogsList}
                  formData={{ case_type: activeTab }}
                  columns={importLogsColumns}
                  totalData={importLogs}
                  totalCount={count}
                  loadingStatus={tableLoadingStatus}
                  pageSizeOptions={DEFAULT_IMPORT_LOGS_PAGE_SIZE_OPTIONS}
                  pageSize={currentPageSize}
                  currentPage={currentPage}
                  onPageChange={({ page, pageSize }) => {
                    setCurrentPage(page)
                    setCurrentPageSize(pageSize)
                  }}
                  onRow={record => ({
                    onClick: event => {
                      if (event.target.closest('.do-not-navigate')) {
                        return
                      }
                      history.push({
                        pathname: `/data-sync-log/${record.id}`,
                        state: {
                          returnTab: activeTab,
                          returnPage: currentPage,
                          returnPageSize: currentPageSize,
                        },
                      })
                    },
                    style: { cursor: 'pointer' },
                  })}
                />
              )}
            </>
          </Tab.TabPane>
        ))}
      </Tabs>
    </PageLayout>
  )
}

export default ImportLogs
